import { useCallback, useMemo } from "react";
import { motion } from "framer-motion";

import { DEFAULT_EASE, stringFallback, truncate } from "../shared";
import { containsJapanese } from "../../../utilities";
import { DropInterface, SnkrsCalProps } from "../screens/SnkrsCal";
import { SnkrsDropQr } from "./SnkrsDropQr";
import { SNKRS_URL, dateFormat } from "./SnkrsCalHelpers";
import SnkrsTypography from "./SnkrsTypography";

interface SnkrsCalDetailDropContentProps {
    show: boolean;
    drop: DropInterface;
    cta_label?: string;
    qr_url?: string;
    hide_dates?: boolean;
    locale?: string;
    available_copy?: string;
    concept: SnkrsCalProps["concept"];
    qr_large?: boolean;
}

export default function SnkrsCalDetailDropContent(props: SnkrsCalDetailDropContentProps) {
    const { show, qr_url, concept, drop, cta_label, locale, available_copy, hide_dates, qr_large } = props;

    const droppingAt = useMemo(() => dateFormat(drop, { locale, available_copy }), [drop, hide_dates]);

    const hasJapanese = useCallback((content: string) => {
        return containsJapanese(content);
    }, [drop]);

    const url = stringFallback(drop?.qr_link, stringFallback(qr_url, SNKRS_URL));
    const titleHasJapanese = hasJapanese(drop?.subtitle);
    const wofFont = titleHasJapanese ? "jordan" : "jordan-condensed";
    const wofSize = titleHasJapanese ? 125 : 170;
    const wofLineHeight = titleHasJapanese ? "90%" : "80%";
    const wofColorDescriptionFont = hasJapanese(drop?.colorDescription) ? "pp-ultralight" : "pp-ultralight-italic";

    const TITLE_SHRINK_AT_LENGTH = 30;

    const titleText = drop?.subtitle?.toUpperCase() ?? "";
    const baseTitleSize = concept === "wof" ? wofSize : 150;
    const titleSize = titleText.length > TITLE_SHRINK_AT_LENGTH
        ? Math.round(baseTitleSize * Math.max(0.6, TITLE_SHRINK_AT_LENGTH / titleText.length))
        : baseTitleSize;

    const DATE_SHRINK_AT_LENGTH = 30;

    const dateText = droppingAt ?? "";
    const dateIsLong = dateText.length > DATE_SHRINK_AT_LENGTH;
    const dateSize = dateIsLong ? 32 : 42;

    return (
        <motion.div
            initial={false}
            animate={{
                y: show ? "0%" : "100%",
            }}
            transition={{
                ease: DEFAULT_EASE,
                duration: 1,
            }}
            style={{
                zIndex: 2,
                position: "absolute",
                bottom: 0,
                width: "100%",
                height: 840,
                padding: "33px 40px",
                background: "white",
            }}
        >
            <SnkrsTypography
                variant={concept === "wof" ? wofFont : "futura"}
                color={"black"}
                size={titleSize}
                style={{
                    marginTop: 19,
                    lineHeight: concept === "wof" ? wofLineHeight : "85%",
                }}
            >
                {truncate(drop?.subtitle?.toUpperCase(), titleHasJapanese ? 30 : 60)}
            </SnkrsTypography>

            <SnkrsTypography
                size={42}
                style={{
                    marginTop: concept === "wof" ? 10 : 60,
                    fontWeight: concept !== "wof" && 500,
                }}
                variant={concept === "wof" ? wofColorDescriptionFont : "neue"}
                color={"black"}
            >
                {truncate(drop?.colorDescription, hasJapanese(drop?.colorDescription) ? 20 : 35)}
            </SnkrsTypography>
            {hide_dates ? null : (
                <SnkrsTypography
                    size={dateSize}
                    style={{
                        marginTop: 17,
                        fontWeight: concept !== "wof" && 500,
                        lineHeight: dateIsLong ? "120%" : undefined,
                    }}
                    variant={concept === "wof" ? "pp-ultralight" : "neue"}
                    color={"black"}
                >
                    {droppingAt}
                </SnkrsTypography>
            )}
            {url && !drop?.hide_qr ? (
                <SnkrsDropQr
                    concept={concept}
                    cta={cta_label}
                    url={url}
                    large={qr_large}
                />
            ) : null}
        </motion.div>
    );
}
